// src/utils/networkMonitor.js
//
// Measures the student's real download speed during an exam, at the interval
// set in the exam's proctoring settings, and reports it to
// /student/network-create/.
//
// Module-level (not component-level) on purpose: App.vue swaps the route to
// NetworkDisconnect whenever the browser goes offline, which unmounts the
// exam page -- a component timer would die exactly when we need it most.
import { apiPost } from "@/utils/api";
import store from "@/store";

// Used when the exam has no interval configured.
const DEFAULT_CHECK_INTERVAL_SECONDS = 60;
// Same floor the exam form enforces, in case an older value slips through.
const MIN_CHECK_INTERVAL_SECONDS = 10;
// Below this the connection is reported as SLOW (LiveKit camera + screen
// share stops being reliable around here).
export const SLOW_THRESHOLD_MBPS = 1;
// Give up on a probe after this long; speed is then computed from the bytes
// that did arrive, so very slow links still get a real number.
const PROBE_TIMEOUT_MS = 15 * 1000;
// 512 KB of random bytes in /public -- random so gzip can't shrink it and
// inflate the measured speed.
const PROBE_URL = `${process.env.BASE_URL}network-probe.bin`;

let _enrollmentId = null;
let _intervalId = null;
let _intervalMs = null;
let _checkInFlight = false;
let _abortController = null;
// Bumped on every start/stop so a probe that finishes after stop() (or after
// a restart for another exam) can't post a stale reading.
let _generation = 0;
// The server stamps checked_at when a report arrives and can't receive one
// while we're offline, so a drop is held here and sent first on reconnect --
// otherwise network_down_count would never see it.
let _pendingOffline = false;

function round2(n) {
  return Math.round(n * 100) / 100;
}

async function measureSpeedMbps() {
  _abortController = new AbortController();
  const { signal } = _abortController;
  let timedOut = false;
  const timeoutId = setTimeout(() => {
    timedOut = true;
    _abortController.abort();
  }, PROBE_TIMEOUT_MS);

  let bytes = 0;
  let startedAt = 0;
  try {
    // Unique URL + no-store so neither the browser nor a proxy serves it
    // from cache.
    const res = await fetch(`${PROBE_URL}?t=${Date.now()}`, {
      cache: "no-store",
      signal,
    });
    if (!res.ok || !res.body) {
      throw new Error(`Probe failed with HTTP ${res.status}`);
    }

    // Timed from the first response byte, so latency (TTFB) doesn't count
    // against throughput.
    startedAt = performance.now();
    const reader = res.body.getReader();
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      bytes += value.byteLength;
    }
  } catch (err) {
    // A timed-out probe that did receive data is a slow link, not a dead one.
    if (!(timedOut && bytes > 0)) {
      return null;
    }
  } finally {
    clearTimeout(timeoutId);
    _abortController = null;
  }

  const seconds = (performance.now() - startedAt) / 1000;
  if (!bytes || seconds <= 0) return null;
  return (bytes * 8) / seconds / 1e6;
}

async function postReading(status, speedMbps, generation) {
  if (generation !== _generation || !_enrollmentId) return false;

  const payload = {
    student_profile: store.getters.user?.student_profile_id,
    exam_enrollment: _enrollmentId,
    network_status: status,
  };
  if (status !== "OFFLINE") {
    // Backend field is decimal(8, 2) and rejects more than 2 places.
    payload.network_speed_mbps = round2(speedMbps);
  }

  try {
    await apiPost("/student/network-create/", payload);
    return true;
  } catch (err) {
    console.error("[NetworkMonitor] Failed to save network check:", err);
    return false;
  }
}

async function runCheck() {
  if (_checkInFlight || !_enrollmentId) return;
  _checkInFlight = true;
  const generation = _generation;

  try {
    const speedMbps = navigator.onLine ? await measureSpeedMbps() : null;
    if (generation !== _generation) return;

    if (speedMbps === null) {
      _pendingOffline = true;
      // Try anyway -- the browser can report offline while a route out
      // still exists. If it fails, the drop stays queued.
      if (await postReading("OFFLINE", null, generation)) {
        _pendingOffline = false;
      }
      return;
    }

    if (_pendingOffline) {
      if (!(await postReading("OFFLINE", null, generation))) return;
      _pendingOffline = false;
    }

    const status = speedMbps < SLOW_THRESHOLD_MBPS ? "SLOW" : "ONLINE";
    await postReading(status, speedMbps, generation);
  } finally {
    _checkInFlight = false;
  }
}

function handleOffline() {
  // Record the drop the moment the browser notices it rather than waiting up
  // to a minute for the next scheduled check.
  _pendingOffline = true;
  if (_abortController) _abortController.abort();
}

function handleOnline() {
  // Report the drop and a fresh reading as soon as the network is back.
  runCheck();
}

// Starts (or keeps running) the monitor for the exam enrollment the launch
// page stored in localStorage. Safe to call on every exam-flow route change.
export function startNetworkMonitor() {
  const enrollmentId = localStorage.getItem("exam_enrollment_id");
  if (
    !enrollmentId ||
    enrollmentId === "null" ||
    enrollmentId === "undefined"
  ) {
    console.warn("[NetworkMonitor] No exam enrollment found, not starting.");
    return;
  }

  const intervalMs = readCheckIntervalMs();
  if (intervalMs === null) {
    // 0 in the exam's proctoring settings means network checks are off.
    stopNetworkMonitor();
    return;
  }

  if (
    _intervalId &&
    enrollmentId === _enrollmentId &&
    intervalMs === _intervalMs
  ) {
    return;
  }
  stopNetworkMonitor();

  _enrollmentId = enrollmentId;
  _intervalMs = intervalMs;
  _generation++;
  window.addEventListener("offline", handleOffline);
  window.addEventListener("online", handleOnline);
  _intervalId = setInterval(runCheck, intervalMs);
  runCheck();
}

// The exam's network_check_interval_seconds, stored by the launch page.
// Returns null when checks are switched off (0).
function readCheckIntervalMs() {
  const raw = localStorage.getItem("network_check_interval_seconds");
  const seconds = raw === null || raw === "" ? NaN : Number(raw);

  if (!Number.isFinite(seconds) || seconds < 0) {
    return DEFAULT_CHECK_INTERVAL_SECONDS * 1000;
  }
  if (seconds === 0) return null;
  return Math.max(seconds, MIN_CHECK_INTERVAL_SECONDS) * 1000;
}

export function stopNetworkMonitor() {
  if (_intervalId) {
    clearInterval(_intervalId);
    _intervalId = null;
  }
  _intervalMs = null;
  window.removeEventListener("offline", handleOffline);
  window.removeEventListener("online", handleOnline);
  if (_abortController) _abortController.abort();
  _generation++;
  _enrollmentId = null;
  _checkInFlight = false;
  _pendingOffline = false;
}

// Closing, reloading or navigating the exam window away fires pagehide:
// stop there so an in-flight probe/POST is cancelled instead of finishing as
// the page dies. If the browser keeps the page in its back/forward cache and
// restores it (pageshow with persisted), the router doesn't fire again, so
// resume here -- but only if it was running when the page was hidden.
let _resumeOnShow = false;

window.addEventListener("pagehide", () => {
  _resumeOnShow = Boolean(_intervalId);
  stopNetworkMonitor();
});

window.addEventListener("pageshow", (event) => {
  if (event.persisted && _resumeOnShow) {
    _resumeOnShow = false;
    startNetworkMonitor();
  }
});
